/**
 * Self-heal for stale deployments. Chunk filenames are content-hashed, so after a deploy any client
 * still holding the previous index.html or main.js requests chunk hashes that no longer exist on the
 * server. The SPA navigationFallback answers those with index.html (text/html), which the browser
 * rejects for module scripts. Detect that failure shape and reload once to pick up the new build.
 *
 * The sessionStorage guard key is shared with the inline pre-boot handler in src/index.html — keep
 * them in sync so the two layers can never double-reload.
 */
const RELOAD_GUARD_KEY = 'yt-chunk-reload-at';
const RELOAD_GUARD_WINDOW_MS = 60_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  'ChunkLoadError',
  'Loading chunk',
  // Chrome
  'Failed to fetch dynamically imported module',
  // Firefox
  'error loading dynamically imported module',
  // Safari
  'Importing a module script failed',
  // MIME mismatch from the SPA fallback serving HTML for a missing chunk
  'Expected a JavaScript'
];

// Seam for tests — window.location.reload/assign are non-configurable and cannot be spied on directly
export const chunkReloadLocation = {
  reload: () => window.location.reload(),
  assign: (url: string) => window.location.assign(url)
};

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message = typeof error === 'string' ? error : ((error as { message?: string }).message ?? '');
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Reloads the app to recover from a stale-chunk failure, at most once per guard window so a genuinely
 * broken deployment degrades to the error state instead of a reload loop. Navigates to targetUrl when
 * provided (the route the user was trying to reach), otherwise reloads in place. Returns true when a
 * reload was initiated.
 */
export function reloadForStaleChunks(targetUrl?: string): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_GUARD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Without the guard a reload could loop forever, so bail out instead
    return false;
  }
  if (targetUrl) {
    chunkReloadLocation.assign(targetUrl);
  } else {
    chunkReloadLocation.reload();
  }
  return true;
}
